/**
 * Names, in words, the phase a setup revision is working through, so a busy
 * state can be read without watching an animation.
 */

import { isSetupRevisionInFlight, type SetupRevisionStatus } from './revisionState';

/** Apply the target in Revit, save and re-read the profile, check readiness. */
export const SetupRevisionPhaseTotal = 3;

export interface SetupRevisionPhase {
  /** Position in the save sequence, counted from 1. */
  step: number;
  totalSteps: number;
  /** Short name of what is running right now. */
  label: string;
  /** One sentence saying what that step is doing. */
  detail: string;
}

type PhaseCopy = Omit<SetupRevisionPhase, 'totalSteps'>;

const PhaseByStatus: Partial<Record<SetupRevisionStatus, PhaseCopy>> = {
  reconciling: {
    step: 1,
    label: 'Updating the Revit project',
    detail: 'PipeEX is creating or updating connector parameters in the active Revit project from the revised setup.',
  },
  profileUnverified: {
    step: 2,
    label: 'Verifying the saved profile',
    detail: 'PipeEX is saving the revised mapping profile and reading it back to verify the stored configuration.',
  },
  readinessChecking: {
    step: 3,
    label: 'Checking project readiness',
    detail: 'PipeEX is checking mapped parameter names, Revit categories, and bindings; this phase does not run PCF export.',
  },
};

/** `null` whenever nothing is running, so callers render no busy state at all. */
export const resolveSetupRevisionPhase = (
  status: SetupRevisionStatus
): SetupRevisionPhase | null => {
  const phase = isSetupRevisionInFlight(status) ? PhaseByStatus[status] : undefined;

  return phase ? { ...phase, totalSteps: SetupRevisionPhaseTotal } : null;
};

/** The text form of progress, so position is never carried by motion alone. */
export const describeSetupRevisionPhase = (phase: SetupRevisionPhase): string =>
  `Step ${phase.step} of ${phase.totalSteps}: ${phase.label}`;
